import React, { lazy, Suspense, useEffect, useRef } from 'react';
import { BrowserRouter as Router, Routes, Route, useLocation } from 'react-router-dom';
import Navbar from './components/Layout/Navbar/Navbar';
import Footer from './components/Layout/Footer/Footer';
import Preloader from './components/UI/Feedback/Preloader';
import ErrorBoundary from './components/UI/Feedback/ErrorBoundary';
import { LanguageProvider, getLanguageBasename, useLanguage } from './contexts/LanguageContext';
import TopProgressBar from './components/UI/Feedback/TopProgressBar';

// ── Heartbeat: báo server rằng tab này còn đang mở ──────────────────────────
function useHeartbeat() {
  const sessionId = useRef(null);
  useEffect(() => {
    // Tạo sessionId duy nhất cho tab này
    try {
      if (!sessionStorage.getItem('__hbid')) {
        sessionStorage.setItem('__hbid', Math.random().toString(36).slice(2) + Date.now().toString(36));
      }
      sessionId.current = sessionStorage.getItem('__hbid');
    } catch (e) {
      // Fallback nếu sessionStorage bị chặn (trình duyệt cũ hoặc chế độ ẩn danh Safari)
      sessionId.current = 'hb_' + Math.random().toString(36).slice(2) + Date.now().toString(36);
    }

    const ping = () => {
      fetch('/api/heartbeat', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ sessionId: sessionId.current })
      }).catch(() => { });
    };

    ping(); // ping ngay khi load
    const interval = setInterval(ping, 20000); // mỗi 20 giây
    return () => clearInterval(interval);
  }, []);
}
// ─────────────────────────────────────────────────────────────────────────────
const Home = lazy(() => import('./pages/Home/Home'));
const Layout = lazy(() => import('./pages/Photo/Layout'));
const TakePhoto = lazy(() => import('./pages/Photo/TakePhoto'));
const Result = lazy(() => import('./pages/Photo/Result'));
const Feedback = lazy(() => import('./pages/Feedback/Feedback'));
const Contact = lazy(() => import('./pages/Contact/Contact'));
const CreateFrames = lazy(() => import('./pages/Guide/CreateFrames/CreateFrames'));
const HowToUse = lazy(() => import('./pages/Guide/HowToUse/HowToUse'));
const About = lazy(() => import('./pages/About/About'));
const ShareView = lazy(() => import('./pages/ShareView/ShareView'));
const Admin = lazy(() => import('./pages/Admin/Admin'));
const NotFound = lazy(() => import('./pages/NotFound/NotFound'));

function AppContent() {
  useHeartbeat(); // 📡 ping server mỗi 20s để báo đang online
  const location = useLocation();
  const isPhotoPage = location.pathname.startsWith('/photo');
  const isAdminPage = location.pathname.startsWith('/adminpanel');
  const isSharePage = location.pathname.startsWith('/share');

  return (
    <div className={`min-h-screen bg-gradient-to-br from-[#e0f6ff] via-[#b3d8f6] to-[#87ceeb] flex flex-col ${isAdminPage ? 'h-screen overflow-hidden' : ''}`}>
      {!isAdminPage && !isSharePage && <Navbar />}
      <main className={`flex-grow ${isAdminPage ? 'w-full h-full overflow-hidden' : isSharePage ? 'w-full' : 'container mx-auto p-4'}`}>
        <Suspense fallback={<Preloader isFallback={true} />}>
          <Routes>
            <Route path="/" element={<Home />} />
            <Route path="/photo" element={<Layout />} />
            <Route path="/photo/take" element={<TakePhoto />} />
            <Route path="/photo/result" element={<Result />} />
            <Route path="/feedback" element={<Feedback />} />
            <Route path="/contact" element={<Contact />} />
            <Route path="/about" element={<About />} />
            <Route path="/guide/create-frames" element={<CreateFrames />} />
            <Route path="/guide/how-to-use" element={<HowToUse />} />
            <Route path="/share/:shareId" element={<ShareView />} />
            <Route path="/adminpanel" element={
              <Suspense fallback={<Preloader isFallback={true} forceShowOverlay={true} />}>
                <Admin />
              </Suspense>
            } />
            <Route path="*" element={<NotFound />} />
          </Routes>
        </Suspense>
      </main>
      {!isPhotoPage && !isAdminPage && !isSharePage && <Footer />}
    </div>
  );
}

function App() {
  return (
    <ErrorBoundary>
      <LanguageProvider>
        <AppWrapper />
      </LanguageProvider>
    </ErrorBoundary>
  );
}

function AppWrapper() {
  const { language } = useLanguage();
  
  return (
    <>
      <Preloader />
      <Router key={language} basename={getLanguageBasename()} future={{ v7_startTransition: true }}>
        <TopProgressBar />
        <AppContent />
      </Router>
    </>
  );
}

export default App;
